import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiAlertTriangle, FiDownload, FiMail, FiSettings, FiUpload, FiX } = FiIcons;

export default function Settings() {
  const { user } = useAuth();
  const [showRestore, setShowRestore] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const fileInput = React.createRef();

  if (user?.system_role !== 'superadmin') return <Navigate to="/" replace />;

  const createBackup = async () => {
    setMessage(''); setBusy(true);
    try { const { exportDataToCSV } = await import('../utils/backupUtils'); await exportDataToCSV(); setMessage('Backup created successfully.'); }
    catch { setMessage('The backup could not be created.'); }
    finally { setBusy(false); }
  };

  const restoreBackup = async (event) => {
    event.preventDefault();
    if (!selectedFile) return;
    setMessage(''); setBusy(true);
    try { const { importDataFromCSV } = await import('../utils/backupUtils'); await importDataFromCSV(selectedFile); setShowRestore(false); setSelectedFile(null); setMessage('Backup restored successfully.'); }
    catch { setMessage('The backup could not be restored. Check the file and try again.'); }
    finally { setBusy(false); }
  };

  return <div className="p-6">
    <div className="mb-8">
      <Link to="/" className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 mb-4"><SafeIcon icon={FiArrowLeft} className="w-5 h-5" /><span>Back to Dashboard</span></Link>
      <div className="flex items-center space-x-3"><SafeIcon icon={FiSettings} className="w-8 h-8 text-primary-600" /><div><h1 className="text-3xl font-bold text-gray-900">App Settings</h1><p className="text-gray-600">Superadmin controls and data management</p></div></div>
    </div>

    {message && <div className="mb-6 bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg">{message}</div>}

    <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
      <div className="flex items-center mb-3"><SafeIcon icon={FiMail} className="w-5 h-5 text-primary-600 mr-2" /><h2 className="text-xl font-semibold">Login email delivery</h2></div>
      <p className="text-gray-600">One-time sign-in codes are delivered by the managed Emailit integration from <strong>bpaportal@bpasuccess.com</strong>. Delivery configuration is managed outside the browser so account credentials are never stored in D1 or exposed to portal users.</p>
    </motion.section>

    <motion.section initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-lg border border-gray-200 p-6">
      <h2 className="text-xl font-semibold mb-3">Data management</h2>
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex"><SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-amber-600 mr-2 mt-0.5" /><p className="text-sm text-amber-800">Backup files contain sensitive member information. Store them securely. Restoring a backup changes current portal data.</p></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <button onClick={createBackup} disabled={busy} className="flex items-center justify-center space-x-2 px-4 py-3 bg-green-600 text-white rounded-lg disabled:opacity-50"><SafeIcon icon={FiDownload} className="w-4 h-4" /><span>Create backup</span></button>
        <button onClick={() => setShowRestore(true)} disabled={busy} className="flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg disabled:opacity-50"><SafeIcon icon={FiUpload} className="w-4 h-4" /><span>Restore backup</span></button>
      </div>
    </motion.section>

    <AnimatePresence>{showRestore && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"><motion.div initial={{ opacity: 0, scale: .95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: .95 }} className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
      <div className="flex justify-between items-center mb-4"><h3 className="text-xl font-semibold">Restore from backup</h3><button onClick={() => setShowRestore(false)}><SafeIcon icon={FiX} className="w-5 h-5" /></button></div>
      <form onSubmit={restoreBackup} className="space-y-4"><input ref={fileInput} type="file" accept=".csv" onChange={(event) => setSelectedFile(event.target.files[0] || null)} className="w-full border border-gray-300 rounded-lg p-3" /><div className="flex gap-3"><button type="button" onClick={() => setShowRestore(false)} className="flex-1 px-4 py-2 border rounded-lg">Cancel</button><button disabled={!selectedFile || busy} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50">{busy ? 'Restoring…' : 'Restore'}</button></div></form>
    </motion.div></div>}</AnimatePresence>
  </div>;
}
